/**
 * Multi-Lot Data Fetching Hooks
 * Hooks for fetching and aggregating data from multiple parking lots in parallel
 */

import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  lotDailyDataService,
  lotService,
  sessionService,
  violationService,
  notificationService,
  registryService,
} from "../utils/api";
import {
  aggregateAnalytics,
  aggregateHourlyData,
  aggregateSessions,
  aggregateViolations,
  aggregateNotifications,
  createLotNameMap,
  DashboardMetrics,
  HourlyMetrics,
  ParkingSession,
  SessionWithLot,
  ViolationPlate,
  ViolationPlateWithLot,
  NotificationItem,
  NotificationWithLot,
} from "../utils/multiLotAggregation";
import { UserLot } from "../types";

/**
 * Hook for fetching analytics from multiple lots in parallel
 */
export const useMultiLotAnalytics = (
  lotIds: string[],
  startDate: string,
  endDate: string,
  timeframe: "day" | "week" | "month" | "year",
  selectedLots: UserLot[] = []
) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "analytics", startDate, endDate] as const,
      queryFn: async () => {
        const data = await lotDailyDataService.getDashboardMetrics(
          lotId,
          startDate,
          endDate
        );
        return data as DashboardMetrics[];
      },
      enabled: !!lotId && !!startDate && !!endDate && lotIds.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const errors = queries
    .map((q) => q.error)
    .filter(Boolean) as (Error | null)[];

  // Aggregate the data from all lots (memoized to avoid re-renders)
  const data = useMemo(() => {
    if (isLoading) return undefined;
    const allData = queries.map((q) => q.data || []);
    return aggregateAnalytics(allData, lotIds);
  }, [isLoading, lotIds.join(","), queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    errors,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching hourly data from multiple lots in parallel
 */
export const useMultiLotHourlyData = (
  lotIds: string[],
  date: string,
  timeframe: "day" | "week" | "month" | "year"
) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "hourly", date] as const,
      queryFn: async () => {
        const data = await lotDailyDataService.getLotHourlyByDate(lotId, date);
        return data as { success: boolean; data: HourlyMetrics[] };
      },
      enabled: !!lotId && !!date && timeframe === "day" && lotIds.length > 0,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const errors = queries
    .map((q) => q.error)
    .filter(Boolean) as (Error | null)[];

  const data = useMemo(() => {
    if (isLoading || timeframe !== "day") return undefined;
    const allData = queries.map((q) => q.data || { success: true, data: [] });
    return aggregateHourlyData(allData);
  }, [isLoading, timeframe, queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    errors,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching sessions from multiple lots in parallel
 */
export const useMultiLotSessions = (
  lotIds: string[],
  selectedLots: UserLot[]
) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "sessions"] as const,
      queryFn: async () => {
        const data = await sessionService.getSessionsByLot(lotId);
        return data as ParkingSession[];
      },
      enabled: !!lotId && lotIds.length > 0,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  const data = useMemo(() => {
    if (isLoading) return undefined;
    const allData = queries.map((q) => q.data || []);
    const lotNames = createLotNameMap(selectedLots);
    return aggregateSessions(allData, lotNames);
  }, [isLoading, selectedLots, queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isError,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching violations from multiple lots in parallel
 */
export const useMultiLotViolations = (
  lotIds: string[],
  selectedLots: UserLot[]
) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "violations"] as const,
      queryFn: async () => {
        const data = await violationService.getAllViolationsByLot(lotId);
        return data as ViolationPlate[];
      },
      enabled: !!lotId && lotIds.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const errors = queries
    .map((q) => q.error)
    .filter(Boolean) as (Error | null)[];

  const data = useMemo(() => {
    if (isLoading) return undefined;
    const allData = queries.map((q) => q.data || []);
    const lotNames = createLotNameMap(selectedLots);
    return aggregateViolations(allData, lotIds, lotNames);
  }, [isLoading, selectedLots, lotIds.join(","), queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    errors,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching current occupancy from multiple lots in parallel
 */
export const useMultiLotOccupancy = (lotIds: string[]) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "currentOccupancy"] as const,
      queryFn: async () => {
        const data = await lotService.getCurrentOccupancy(lotId);
        return data as number | null;
      },
      enabled: !!lotId && lotIds.length > 0,
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  // Sum all occupancies
  const totalOccupancy = useMemo(() => {
    if (isLoading) return null;
    return queries.reduce((sum, q) => sum + (q.data || 0), 0);
  }, [isLoading, queries.map((q) => q.data).join("|")]);

  return {
    data: totalOccupancy,
    isLoading,
    isError,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching lot details from multiple lots in parallel
 */
export const useMultiLotDetails = (lotIds: string[]) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId] as const,
      queryFn: async () => {
        const data = await lotService.getLotById(lotId);
        return data;
      },
      enabled: !!lotId && lotIds.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  // Combine lot data
  const data = useMemo(() => queries.map((q) => q.data).filter(Boolean), [queries.map((q) => q.data).join("|")]);

  // Calculate combined capacity
  const totalCapacity = useMemo(
    () =>
      data.reduce(
        (sum, lot) => sum + (lot?.lotCapacity || 0),
        0
      ),
    [data]
  );

  return {
    data,
    totalCapacity,
    isLoading,
    isError,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching devices from multiple lots in parallel
 */
export const useMultiLotDevices = (lotIds: string[]) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "devices"] as const,
      queryFn: async () => {
        const data = await (await import("../utils/api")).devicesService.getDevicesByLot(lotId);
        return data;
      },
      enabled: !!lotId && lotIds.length > 0,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const errors = queries
    .map((q) => q.error)
    .filter(Boolean) as (Error | null)[];

  // Combine all devices
  const data = useMemo(() => queries.flatMap((q) => q.data || []), [queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    errors,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching plate registry from multiple lots in parallel
 */
export const useMultiLotRegistry = (lotIds: string[]) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "registry"] as const,
      queryFn: async () => {
        const data = await registryService.getRegistryByLot(lotId);
        return data || [];
      },
      enabled: !!lotId && lotIds.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const errors = queries
    .map((q) => q.error)
    .filter(Boolean) as (Error | null)[];

  // Merge registry entries (dedupe by plate number, keep first occurrence)
  const data = useMemo(() => {
    if (isLoading) return undefined;
    const plateMap = new Map();
    queries.forEach((q, index) => {
      const entries = q.data || [];
      const lotId = lotIds[index];
      entries.forEach((entry: any) => {
        if (!plateMap.has(entry.plateNumber)) {
          plateMap.set(entry.plateNumber, { ...entry, lotId });
        }
      });
    });
    return Array.from(plateMap.values());
  }, [isLoading, lotIds.join(","), queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isFetching,
    isError,
    errors,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};

/**
 * Hook for fetching ignored ALPR strings from multiple lots
 */
export const useMultiLotIgnoredStrings = (lotIds: string[]) => {
  const queries = useQueries({
    queries: lotIds.map((lotId) => ({
      queryKey: ["lot", lotId, "ignoredStrings"] as const,
      queryFn: async () => {
        const { ignoredAlprService } = await import("../utils/api");
        const data = await ignoredAlprService.getIgnoredStringsByLot(lotId);
        return data || [];
      },
      enabled: !!lotId && lotIds.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  // Get unique strings across all lots
  const data = useMemo(() => {
    if (isLoading) return undefined;
    const uniqueStrings = new Set<string>();
    queries.forEach((q) => {
      const strings = q.data || [];
      strings.forEach((item: any) => {
        uniqueStrings.add(item.ignoredString || item);
      });
    });
    return Array.from(uniqueStrings).sort();
  }, [isLoading, queries.map((q) => q.data).join("|")]);

  return {
    data,
    isLoading,
    isError,
    refetch: () => queries.forEach((q) => q.refetch()),
  };
};
